/**
 * PropertyTemplateEditor Component
 * 
 * Form for creating and editing property templates.
 * Shows template info and property mappings.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Tabs,
  Typography,
  message,
  Popconfirm,
  Alert
} from 'antd';
import {
  SaveOutlined,
  DeleteOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { PropertyLevelService } from '../../services/api/propertyLevelService';
import { 
  GlobalPropertyTemplate, 
  CreateTemplateRequest, 
  UpdateTemplateRequest 
} from '../../types/propertyLevel';
import { TemplatePropertyMapper } from './TemplatePropertyMapper';
import styles from './PropertyTemplateEditor.module.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface PropertyTemplateEditorProps {
  template?: GlobalPropertyTemplate | null;
  isCreating?: boolean;
  onSave?: (template: GlobalPropertyTemplate) => void;
  onDelete?: () => void;
  onCancel?: () => void;
}

export const PropertyTemplateEditor: React.FC<PropertyTemplateEditorProps> = ({
  template,
  isCreating = false,
  onSave,
  onDelete,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [currentTemplate, setCurrentTemplate] = useState<GlobalPropertyTemplate | null>(template || null);
  const [hasChanges, setHasChanges] = useState(false);

  // Update form when template changes
  useEffect(() => {
    if (template) {
      form.setFieldsValue({
        template_key: template.template_key,
        template_name: template.template_name,
        description: template.description || '',
        artifact_type_hint: template.artifact_type_hint || ''
      });
      setCurrentTemplate(template);
    } else if (isCreating) {
      form.resetFields();
      setCurrentTemplate(null);
    }
    setHasChanges(false);
  }, [template, isCreating, form]);

  // Fetch full template data if needed
  const fetchTemplateData = useCallback(async (templateId: string) => {
    try {
      console.log(`[PropertyTemplateEditor] fetchTemplateData called for template ${templateId}`);
      // Use template.id (UUID) instead of template.template_key
      const fullTemplate = await PropertyLevelService.getTemplate(templateId);
      console.log(`[PropertyTemplateEditor] Fetched template:`, {
        id: fullTemplate.id,
        template_key: fullTemplate.template_key,
        template_name: fullTemplate.template_name,
        propertiesCount: fullTemplate.properties?.length || 0,
        properties: fullTemplate.properties?.map(p => p.property_key) || []
      });
      setCurrentTemplate(fullTemplate);
    } catch (error) {
      console.error('[PropertyTemplateEditor] Error fetching template:', error);
    }
  }, []);

  useEffect(() => {
    // Use template.id (UUID) instead of template.template_key
    // Fetch template data if we have an ID but no properties loaded yet
    // Check for both undefined and empty array cases
    if (template?.id) {
      const hasProperties = template.properties && Array.isArray(template.properties) && template.properties.length > 0;
      if (!hasProperties) {
        console.log(`[PropertyTemplateEditor] Template ${template.id} has no properties, fetching full template data`);
        void fetchTemplateData(template.id);
      } else {
        console.log(`[PropertyTemplateEditor] Template ${template.id} already has ${template.properties?.length || 0} properties, skipping fetch`);
      }
    }
  }, [template, fetchTemplateData]);

  // Handle form value changes
  const handleValuesChange = () => {
    setHasChanges(true);
  };

  // Handle save
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      let savedTemplate: GlobalPropertyTemplate;

      if (isCreating) {
        const createData: CreateTemplateRequest = {
          template_key: values.template_key,
          template_name: values.template_name,
          description: values.description || undefined,
          artifact_type_hint: values.artifact_type_hint || undefined
        };
        savedTemplate = await PropertyLevelService.createTemplate(createData);
        void message.success('Template created successfully');
      } else if (template) {
        const updateData: UpdateTemplateRequest = {
          template_name: values.template_name,
          description: values.description || undefined,
          artifact_type_hint: values.artifact_type_hint || undefined
        };
        // Use template.id (UUID) instead of template.template_key
        savedTemplate = await PropertyLevelService.updateTemplate(
          template.id, 
          updateData
        );
        void message.success('Template updated successfully');
      } else {
        throw new Error('No template to save');
      }

      setCurrentTemplate(savedTemplate);
      setHasChanges(false);

      if (onSave) {
        onSave(savedTemplate);
      }
    } catch (error: any) {
      console.error('Error saving template:', error);
      void message.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!template) return;

    try {
      setDeleting(true);
      // Use template.id (UUID) instead of template.template_key
      await PropertyLevelService.deleteTemplate(template.id);
      void message.success('Template deleted successfully');
      if (onDelete) {
        onDelete();
      }
    } catch (error: any) {
      console.error('Error deleting template:', error);
      void message.error(error.response?.data?.detail || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  // Handle property mappings update
  const handlePropertiesUpdate = async () => {
    // Use template.id (UUID) instead of template.template_key
    // Also try currentTemplate.id if template.id is not available
    const templateId = template?.id || currentTemplate?.id;
    if (templateId) {
      console.log(`[PropertyTemplateEditor] handlePropertiesUpdate called, fetching template ${templateId}`);
      await fetchTemplateData(templateId);
      console.log(`[PropertyTemplateEditor] Template data refreshed, currentTemplate properties count:`, currentTemplate?.properties?.length || 0);
    } else {
      console.warn('[PropertyTemplateEditor] handlePropertiesUpdate called but no template ID available');
    }
  };

  // Render empty state
  if (!template && !isCreating) {
    return (
      <Card className={styles.editorCard}>
        <div className={styles.emptyState}>
          <Title level={4}>Select a Template</Title>
          <Text type="secondary">
            Select a template from the list to view and edit its details,
            or create a new template.
          </Text>
        </div>
      </Card>
    );
  }

  const tabItems = [
    {
      key: 'details',
      label: 'Details',
      children: (
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleValuesChange}
          className={styles.form}
        >
          <Form.Item
            name="template_key"
            label="Template Key"
            rules={[
              { required: true, message: 'Template key is required' },
              { 
                pattern: /^[a-z0-9_]+$/, 
                message: 'Key must be lowercase alphanumeric with underscores' 
              }
            ]}
          >
            <Input 
              placeholder="e.g., invoice_standard" 
              disabled={!isCreating}
            />
          </Form.Item>

          <Form.Item
            name="template_name"
            label="Template Name"
            rules={[{ required: true, message: 'Template name is required' }]}
          >
            <Input placeholder="e.g., Standard Invoice Template" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea 
              rows={3} 
              placeholder="Describe what this template is for" 
            />
          </Form.Item>

          <Form.Item
            name="artifact_type_hint"
            label="Artifact Type Hint"
            tooltip="Suggest which artifact types this template is designed for"
          >
            <Input placeholder="e.g., invoice, payment, resource" />
          </Form.Item>
        </Form>
      )
    },
    ...(!isCreating && currentTemplate ? [{
      key: 'properties',
      label: `Properties (${currentTemplate.properties?.length || 0})`,
      children: (
        <TemplatePropertyMapper
          key={currentTemplate.id}  // Force re-render when template changes
          template={currentTemplate}
          onUpdate={handlePropertiesUpdate}
        />
      )
    }] : [])
  ];

  return (
    <Card
      className={styles.editorCard}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {isCreating ? 'Create Template' : `Template: ${template?.template_name}`}
        </Title>
      }
      extra={
        <Space>
          {hasChanges && (
            <Alert 
              message="Unsaved changes" 
              type="warning" 
              showIcon 
              style={{ padding: '4px 12px' }} 
            />
          )}
          <Button onClick={onCancel} icon={<CloseOutlined />}>
            Cancel
          </Button>
          {!isCreating && template && (
            <Popconfirm
              title="Delete Template"
              description="Are you sure you want to delete this template?"
              onConfirm={handleDelete}
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
            >
              <Button 
                danger 
                icon={<DeleteOutlined />}
                loading={deleting}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            {isCreating ? 'Create' : 'Save'}
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </Card>
  );
};

export default PropertyTemplateEditor;
